import { useState, useRef, useCallback } from 'react';

interface UseScreenRecorderReturn {
    recording: boolean;
    error: boolean;
    screenFileName: string;
    videoURL: string | null;
    recordFile: File | null;
    stopRecording: () => void;
    resetRecording: () => void;
    setScreenRecordChunk: () => void;
    startRecording: () => Promise<void>;
}

export const useScreenRecorder = (): UseScreenRecorderReturn => {
    const chunksRef = useRef<Blob[]>([]);
    const recordCountRef = useRef<number>(1);
    const streamRef = useRef<MediaStream | null>(null); // Ref to hold the screen-sharing stream
    const [error, setError] = useState(false);
    const [recording, setRecording] = useState<boolean>(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const [videoURL, setVideoURL] = useState<string | null>(null);
    const [recordFile, setRecordFile] = useState<File | null>(null);

    const startRecording = useCallback(async () => {
        try {
            if (!streamRef.current) {
                const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
                streamRef.current = stream; // Save the stream reference
            }

            mediaRecorderRef.current = new MediaRecorder(streamRef.current, { mimeType: 'video/webm;codecs=vp8' });
            chunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event: BlobEvent) => {
                if (event.data.size > 0) {
                    // chunksRef.current.push(event.data);
                    const file = new File([event.data], `screen_record_part_${recordCountRef.current}.webm`, { type: "video/webm" });
                    setRecordFile(file);
                    recordCountRef.current = recordCountRef.current + 1;
                }
            };

            mediaRecorderRef.current.onstop = () => {
                // const blob = new Blob(chunksRef.current, { type: 'video/webm;codecs=vp8' });
                // const file = new File([blob], `screenRecord.webm`, { type: "video/webm;codecs=vp8" });
                setError(true);
                setRecording(false);
                // setRecordFile(file);
            };

            mediaRecorderRef.current.start();
            setRecording(true);
            setError(false);
        } catch (err) {
            setError(true);
            console.error('Error starting screen recording:', err);
        }
    }, []);

    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            setRecording(false);

            // Stop all tracks of the stream to stop screen sharing
            streamRef.current?.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
    }, [recording]);

    const resetRecording = useCallback(() => {
        setVideoURL(null);
        setError(false);
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
            mediaRecorderRef.current = null;
        }
    }, []);

    const setScreenRecordChunk = () => {
        if (!mediaRecorderRef.current) return;
        mediaRecorderRef.current.stop();
    }

    // const setScreenRecordChunk = useCallback(() => {
    //     try {
    //         if (!mediaRecorderRef.current) return;

    //         mediaRecorderRef.current.requestData();
    //         const blob = new Blob(chunksRef.current, { type: 'video/webm;codecs=vp8' });
    //         const file = new File([blob], `screen_record_part_${recordCountRef.current}.webm`, { type: "video/webm" });
    //         setRecordFile(file);
    //         chunksRef.current = [];
    //         recordCountRef.current = recordCountRef.current + 1;

    //         const url = URL.createObjectURL(blob);
    //         const a = document.createElement("a");
    //         document.body.appendChild(a);
    //         a.href = url;
    //         a.download = `screen_record_part_${recordCountRef.current}.webm`;
    //         a.click();
    //         window.URL.revokeObjectURL(url);
    //     } catch (err) {
    //         console.error('Error uploading recording:', err);
    //     }
    // }, []);


    return {
        error,
        videoURL,
        recording,
        recordFile,
        stopRecording,
        resetRecording,
        startRecording,
        setScreenRecordChunk,
        screenFileName: `screen_record_part_${recordCountRef.current}.webm`,
    };
};